import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "hitpath_upgrade_nudge_dismissed_v1";

/**
 * UpgradeNudge
 * Tasteful banner that appears for signed-in free users once they've saved
 * `threshold` or more workouts. Dismissible — persisted via localStorage so
 * we never nag the same user twice.
 */
export const UpgradeNudge = ({ user, workoutCount, threshold = 3 }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);

  if (!user) return null;
  if (user.plan && user.plan !== "free") return null;
  if (workoutCount < threshold) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="upgrade-nudge-banner"
      className="relative overflow-hidden rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-transparent backdrop-blur-md px-5 py-4 sm:px-6 sm:py-5"
    >
      <div
        aria-hidden
        className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-amber-400/20 blur-3xl pointer-events-none"
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3 pr-6">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-400/40 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-50 text-base sm:text-lg">
              You&apos;re on a roll — unlock Lucky Pick
            </div>
            <p className="text-sm text-slate-300 mt-0.5 max-w-xl">
              {workoutCount} workouts saved. Upgrade to Pro to run Lucky Pick, the Draw Checker, and daily email digests tuned to your seed.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            data-testid="upgrade-nudge-cta"
            onClick={() => navigate("/upgrade")}
            className="h-10 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold px-5"
          >
            Unlock Pro
          </Button>
        </div>
        <button
          type="button"
          aria-label="Dismiss upgrade banner"
          data-testid="upgrade-nudge-dismiss"
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
